import { useState } from "react";
import { ConfirmModal } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal/index";
import { useFcmRegistration } from "@/shared/notifications/FcmRegistrationProvider";
import { isInstallCapSession } from "@/shared/pwa/lib/interruption-order";

const DISMISSED_KEY = "a2z_notification_prompt_dismissed_at";
/** localStorage: how many times "لاحقًا" was tapped, and when last. */
const LATER_KEY = "a2z_notification_prompt_later_v1";
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

type LaterRecord = { count: number; at: number };

function readLater(): LaterRecord {
  try {
    const raw = localStorage.getItem(LATER_KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<LaterRecord>) : null;
    return {
      count: Number(parsed?.count) || 0,
      at: Number(parsed?.at) || 0,
    };
  } catch {
    return { count: 0, at: 0 };
  }
}

/**
 * Whether the enable prompt stands aside.
 *
 * - Within a session, one "لاحقًا" is enough (sessionStorage).
 * - Across sessions (roundtable 2, B7): the first "لاحقًا" only costs this
 *   session — the prompt asks again next time; from the SECOND "لاحقًا" on it
 *   waits seven days before asking again. Hassan dismissed it "more than 25
 *   times" when it asked every session for as long as permission was undecided.
 */
export function isNotificationPromptSuppressed(now = Date.now()): boolean {
  try {
    if (sessionStorage.getItem(DISMISSED_KEY) !== null) return true;
  } catch {
    /* fall through */
  }
  const later = readLater();
  return later.count >= 2 && now - later.at < WEEK_MS;
}

/** Record a "لاحقًا" (or a close) for the back-off above. */
export function recordNotificationPromptLater(now = Date.now()): void {
  try {
    sessionStorage.setItem(DISMISSED_KEY, String(now));
  } catch {
    /* private browsing */
  }
  try {
    const later = readLater();
    localStorage.setItem(LATER_KEY, JSON.stringify({ count: later.count + 1, at: now }));
  } catch {
    /* private browsing */
  }
}

const BellIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

export function NotificationPromptBanner() {
  const t = useTranslations();
  const { status, lastError, retry, requestPermissionAndRegister } =
    useFcmRegistration();

  // Session suppression applies ONLY to the initial enable prompt.
  // It also stands aside for the one session the install sheet claims (a
  // seller who keeps tapping "لاحقًا" would otherwise never see it); see
  // shared/pwa/lib/interruption-order.ts for the order.
  const [promptDismissed, setPromptDismissed] = useState(
    () => isNotificationPromptSuppressed() || isInstallCapSession(),
  );
  // Session-only suppression for the (live-status) unavailable variant.
  const [unavailableDismissed, setUnavailableDismissed] = useState(false);

  const dismissPrompt = () => {
    recordNotificationPromptLater();
    setPromptDismissed(true);
  };

  const handleEnable = () => {
    // Provider does Notification.requestPermission() + ensureRegistered.
    void requestPermissionAndRegister();
    // Close the prompt as soon as the user decides; live status drives the
    // unavailable variant afterwards if registration can't complete. A
    // decision is not a "later": only this session is suppressed.
    try {
      sessionStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      /* private browsing */
    }
    setPromptDismissed(true);
  };

  // ── Initial enable prompt (permission default) ────────────────────────────
  const showPrompt = status === "prompt" && !promptDismissed;

  // ── Unavailable variant (granted, but registration can't complete) ────────
  // Shown for `offline` or transient/config `error`. Informational (NOT a
  // permission prompt) with a manual Retry. Session-dismissible only.
  const showUnavailable =
    (status === "offline" || status === "error") && !unavailableDismissed;

  const reason = lastError?.reason;
  let unavailableBody: string;
  if (status === "offline" || reason === "offline") {
    unavailableBody = t(keys.notifications.unavailableOfflineBody);
  } else if (reason === "config") {
    // Terminal — retry won't help; avoid a "retrying…" tone.
    unavailableBody = t(keys.notifications.unavailableConfigBody);
  } else {
    unavailableBody = t(keys.notifications.unavailableTransientBody);
  }

  return (
    <>
      <ConfirmModal
        tone="primary"
        icon={BellIcon}
        isOpen={showPrompt}
        onClose={dismissPrompt}
        onConfirm={handleEnable}
        title={t(keys.notifications.promptTitle)}
        subtitle={t(keys.notifications.promptSubtitle)}
        description={t(keys.notifications.promptBody)}
        confirmLabel={t(keys.notifications.promptEnable)}
        cancelLabel={t(keys.notifications.promptLater)}
        closeLabel={t(keys.common.close)}
      />

      <ConfirmModal
        tone="primary"
        icon={BellIcon}
        isOpen={showUnavailable}
        onClose={() => setUnavailableDismissed(true)}
        onConfirm={() => retry()}
        title={t(keys.notifications.unavailableTitle)}
        subtitle={t(keys.notifications.unavailableSubtitle)}
        description={unavailableBody}
        confirmLabel={t(keys.notifications.retry)}
        cancelLabel={t(keys.notifications.promptLater)}
        closeLabel={t(keys.common.close)}
      />
    </>
  );
}
